import {SortingRule} from 'react-table';

import {ApiShareLink} from 'app/modules/Remote/ShareLink';

import {makeTableUrlState} from '../tableUrlState';

export const SELECTION_COLUMN_ID = 'selection';
export const PROPERTY_COLUMN_ID = 'property';
export const TYPE_COLUMN_ID = 'type';
export const CREATED_BY_COLUMN_ID = 'createdBy';
export const CREATED_COLUMN_ID = 'createdAt';
export const LINK_COLUMN_ID = 'link';

export const SORTABLE_COLUMN_IDS = [
  PROPERTY_COLUMN_ID,
  TYPE_COLUMN_ID,
  CREATED_BY_COLUMN_ID,
  CREATED_COLUMN_ID,
];

export const DEFAULT_SORT_BY: SortingRule<ApiShareLink>[] = [{id: CREATED_COLUMN_ID, desc: true}];

// Delete sends its ids as `id` query params. gunicorn rejects a request line over
// 4094 bytes with a plain-text 400 before Flask sees it, and uuids hit that at about
// 100 ids, so the cap stays well under.
export const MAX_SELECTED_SHARE_LINKS = 50;

export function shareLinkTypeLabel(link: Pick<ApiShareLink, 'kind'>): string {
  switch (link.kind) {
    case 'report':
      return 'Report';
    case 'story':
      return 'Story';
    case 'widget':
      return 'Dashboard widget';
    default:
      return 'Map';
  }
}

/** What distinguishes this link from others of its type: a report's title or a map's note. */
export function shareLinkDetail(link: ApiShareLink): string | null {
  if (link.kind === 'widget') {
    return link.title;
  }
  return (link.kind === 'map' ? link.description : link.reportTitle) || null;
}

export function shareLinkCreator(link: Pick<ApiShareLink, 'userName' | 'userEmail'>): string {
  return link.userName || link.userEmail || 'Unknown user';
}

/** The property is null only when the row's config no longer parses. */
export function shareLinkPropertyName(link: ApiShareLink): string {
  return link.kind !== 'widget' && link.propertyName ? link.propertyName : 'Unknown property';
}

/** Share link types with at least one link, alphabetical. */
export function shareLinkTypes(links: ApiShareLink[]): string[] {
  return [...new Set(links.map(shareLinkTypeLabel))].sort();
}

export function shareLinkMatchesSearch(link: ApiShareLink, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return [shareLinkPropertyName(link), shareLinkDetail(link), shareLinkCreator(link)].some(
    (text) => !!text && text.toLowerCase().includes(needle)
  );
}

// Query-string keys for the share links view's filter and sort state.
const FILTER_URL_KEYS: Record<string, string> = {
  [PROPERTY_COLUMN_ID]: 'q',
  [TYPE_COLUMN_ID]: 'type',
};

export const {tableStateFromSearch, searchWithTableState} = makeTableUrlState<ApiShareLink>({
  filterUrlKeys: FILTER_URL_KEYS,
  sortableColumnIds: SORTABLE_COLUMN_IDS,
  defaultSortBy: DEFAULT_SORT_BY,
});
